import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "@/App.css";
import Lenis from "lenis";
import { CartProvider } from "@/context/CartContext";
import { PlatformProvider } from "@/context/PlatformContext";
import { CountryProvider } from "@/context/CountryContext";
import PlatformFlipOverlay from "@/components/PlatformFlipOverlay";
import FloatingPlatformSwitcher from "@/components/FloatingPlatformSwitcher";
// InAppBrowserBanner removed per owner request — the escape schemes are
// unreliable on modern iOS/Android and /checkout-help handles WebView traffic
// at the ad level. Component retained in the repo for future use.
import Analytics from "@/components/Analytics";
import PlatformPicker from "@/components/PlatformPicker";
import PageLocatorOverlay from "@/components/PageLocatorOverlay";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import ExitIntentCapture from "@/components/ExitIntentCapture";
import CheckoutFeedback from "@/components/CheckoutFeedback";
import { useCart } from "@/context/CartContext";
import { usePlatform } from "@/context/PlatformContext";
import { useCountry } from "@/context/CountryContext";
import { Toaster } from "@/components/ui/sonner";
// Home is the landing page — ship it in the initial bundle so first paint
// is immediate on mobile. Every other route is code-split via React.lazy()
// so a shopper landing on "/" doesn't pay the JS cost of Admin/Legal/Fleet
// pages they'll never visit on that session.
import Home from "@/pages/Home";
const Shop = lazy(() => import("@/pages/Shop"));
const Fleet = lazy(() => import("@/pages/Fleet"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const UseCasePage = lazy(() => import("@/pages/UseCasePage"));
const Support = lazy(() => import("@/pages/Support"));
const PlacementGuide = lazy(() => import("@/pages/PlacementGuide"));
const Legal = lazy(() => import("@/pages/Legal"));
const Contact = lazy(() => import("@/pages/Contact"));
const About = lazy(() => import("@/pages/About"));
const TheftStats = lazy(() => import("@/pages/TheftStats"));
const TravelCard = lazy(() => import("@/pages/TravelCard"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminOrders = lazy(() => import("@/pages/AdminOrders"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const AdminSupport = lazy(() => import("@/pages/AdminSupport"));
const PaymentSuccess = lazy(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentCancel })));
const CheckoutHelp = lazy(() => import("@/pages/CheckoutHelp"));
import { USE_CASE_PAGES, FLEET_SEO_SLUGS } from "@/data/pages";

let lenis;

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) lenis?.scrollTo(el, { offset: -90 });
      return;
    }
    lenis?.scrollTo(0, { immediate: true });
  }, [pathname, hash]);
  return null;
};

const AdminAwarePicker = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  // Suppress on the ad landing — CheckoutHelp auto-picks the platform from the
  // shopper's phone UA so the modal would just stall the sales-first hero.
  if (pathname === "/checkout-help" || pathname === "/open") return null;
  return <PlatformPicker />;
};

// CheckoutFeedback appears only on checkout-adjacent routes so homepage /
// marketing visitors never see it. Pulls cart + market context so each entry
// in the admin inbox is self-explanatory without needing to join sessions.
const CheckoutFeedbackMount = () => {
  const { pathname } = useLocation();
  const CKFB_PATHS = ["/shop", "/payment/success", "/payment/cancel"];
  const show = CKFB_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const { items } = useCart();
  const { platform } = usePlatform();
  const { market } = useCountry();
  if (!show) return null;
  return <CheckoutFeedback cartItems={items} platform={platform} country={market?.code || null} />;
};

function App() {
  useEffect(() => {
    lenis = new Lenis({ anchors: true, lerp: 0.09 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <PlatformProvider>
        <CountryProvider>
          <CartProvider>
          <ScrollManager />
          <div className="App min-h-screen bg-tracksy-paper font-body text-tracksy-ink antialiased">
            <Navbar />
            <main>
              {/* Suspense fallback is intentionally minimal — just enough
                  layout so there's no visible jump while the chunked route
                  JS downloads on mobile. */}
              <Suspense fallback={<div className="min-h-[60vh]" />}>
                <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/travel-card" element={<TravelCard />} />
                <Route path="/fleet" element={<Fleet />} />
                {FLEET_SEO_SLUGS.map((s) => (
                  <Route key={s} path={`/${s}`} element={<Fleet />} />
                ))}
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/support" element={<Support />} />
                <Route path="/placement-guide" element={<PlacementGuide />} />
                <Route path="/privacy" element={<Legal slug="privacy" />} />
                <Route path="/terms" element={<Legal slug="terms" />} />
                <Route path="/refund" element={<Legal slug="refund" />} />
                <Route path="/shipping" element={<Legal slug="shipping" />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/australian-theft-statistics" element={<TheftStats />} />
                <Route path="/us-theft-statistics" element={<TheftStats />} />
                <Route path="/admin/webhooks" element={<Admin />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/checkout-feedback" element={<AdminSupport />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/checkout-help" element={<CheckoutHelp />} />
                <Route path="/open" element={<CheckoutHelp />} />
                {Object.keys(USE_CASE_PAGES).map((slug) => (
                  <Route key={slug} path={`/${slug}`} element={<UseCasePage page={USE_CASE_PAGES[slug]} />} />
                ))}
                <Route path="*" element={<Home />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
            <CartDrawer />
            {/* CheckoutFeedback kept dormant per owner request — do not mount in production.
                To re-enable: restore <CheckoutFeedbackMount /> here and the Feedback nav link in AdminAnalytics. */}
            <ExitIntentCapture />
            <PlatformFlipOverlay />
            <FloatingPlatformSwitcher />
            <Analytics />
            <AdminAwarePicker />
            <PageLocatorOverlay />
            <Toaster position="bottom-center" richColors />
          </div>
          </CartProvider>
        </CountryProvider>
      </PlatformProvider>
    </BrowserRouter>
  );
}

export default App;
